import { computed, inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

export interface SessionStateModel {
  authToken: string | null;
  userId: string | null;
  operatorLabel: string | null;
  accountName: string | null;
  restoredAt: string | null;
}

const SESSION_STATE_DEFAULTS: SessionStateModel = {
  authToken: null,
  userId: null,
  operatorLabel: null,
  accountName: null,
  restoredAt: null
};

export class SetSession {
  static readonly type = '[Session] Set';

  constructor(
    public readonly payload: Partial<SessionStateModel> & Pick<SessionStateModel, 'authToken' | 'userId'>
  ) {}
}

export class SetAccountName {
  static readonly type = '[Session] Set Account Name';

  constructor(public readonly accountName: string | null) {}
}

export class ClearSession {
  static readonly type = '[Session] Clear';
}

@State<SessionStateModel>({
  name: 'session',
  defaults: SESSION_STATE_DEFAULTS
})
@Injectable()
export class SessionState {
  @Selector()
  static snapshot(state: SessionStateModel): SessionStateModel {
    return state;
  }

  @Selector()
  static authToken(state: SessionStateModel): string | null {
    return state.authToken;
  }

  @Selector()
  static userId(state: SessionStateModel): string | null {
    return state.userId;
  }

  @Selector()
  static operatorLabel(state: SessionStateModel): string | null {
    return state.operatorLabel;
  }

  @Selector()
  static accountName(state: SessionStateModel): string | null {
    return state.accountName;
  }

  @Selector()
  static isAuthenticated(state: SessionStateModel): boolean {
    return Boolean(state.authToken && state.userId);
  }

  @Action(SetSession)
  setSession(ctx: StateContext<SessionStateModel>, action: SetSession): void {
    ctx.patchState({
      ...action.payload,
      restoredAt: action.payload.restoredAt ?? new Date().toISOString()
    });
  }

  @Action(SetAccountName)
  setAccountName(ctx: StateContext<SessionStateModel>, action: SetAccountName): void {
    ctx.patchState({ accountName: action.accountName });
  }

  @Action(ClearSession)
  clearSession(ctx: StateContext<SessionStateModel>): void {
    ctx.setState(SESSION_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class SessionFacade {
  private readonly store = inject(Store);

  readonly session = this.store.selectSignal(SessionState.snapshot);
  readonly authToken = this.store.selectSignal(SessionState.authToken);
  readonly userId = this.store.selectSignal(SessionState.userId);
  readonly operatorLabel = this.store.selectSignal(SessionState.operatorLabel);
  readonly accountName = this.store.selectSignal(SessionState.accountName);
  readonly isAuthenticated = this.store.selectSignal(SessionState.isAuthenticated);
  readonly authHeaders = computed<Record<string, string> | null>(() => {
    const authToken = this.authToken();
    const userId = this.userId();

    if (!authToken || !userId) {
      return null;
    }

    return {
      'X-Auth-Token': authToken,
      'X-Auth-UserId': userId
    };
  });

  restoreSession(
    payload: Partial<SessionStateModel> & Pick<SessionStateModel, 'authToken' | 'userId'>
  ): void {
    this.store.dispatch(new SetSession(payload));
  }

  setAccountName(accountName: string | null): void {
    this.store.dispatch(new SetAccountName(accountName));
  }

  clearSession(): void {
    this.store.dispatch(new ClearSession());
  }
}
